import clx from 'classnames';
import { MarketplaceOfferJsonldMarketplaceOfferRead } from '@/api/generated';
import Badge from '@/atoms/Badge';
import Button, { BUTTON_VARIANTS } from '@/atoms/Button';
import Spinner from '@/atoms/loader/Spinner';
import Pagination from '@/molecules/Pagination';
import { SIZE, VARIANT_COLOR_ENUM } from '@/types/utils';
import { useFormatPrice } from 'hooks/i18n';
import useUserApi from 'hooks/UserApi/useUserApi';
import { useTranslation } from 'react-i18next';
import useListOffer from './hook';
import { useApiParams, useFeatureFlag } from 'utils/ssr/middleware/hooks/useFeatureFlag';
import { PARAMS_KEYS } from 'utils/ssr/middleware/featureFlagMiddleware';
import { CMS_ROUTING } from 'constants/routes';
import { useConfig } from 'context/Config';

type Props = {
  cardRef: string;
  withPagination?: boolean;
  itemsPerPage: number;
};

const OfferView = ({
  offer,
  onEditOfferClick,
  onBuyOfferClick,
}: {
  offer: MarketplaceOfferJsonldMarketplaceOfferRead;
  onEditOfferClick: (cardOffer: MarketplaceOfferJsonldMarketplaceOfferRead) => void;
  onBuyOfferClick: (cardOffer: MarketplaceOfferJsonldMarketplaceOfferRead) => void;
}) => {
  const { t } = useTranslation();
  const { formatUserCurrency } = useFormatPrice();
  const { user } = useUserApi();
  const isPaymentBlocked = useFeatureFlag(PARAMS_KEYS.PAYMENT_BLOCKED);

  return (
    <div className="bg-neutral-100 flex md:min-w-96 w-full items-center px-4 py-2 gap-2 rounded-sm text-neutral-900">
      <Badge size={SIZE.MD} variant={VARIANT_COLOR_ENUM.NEUTRAL}>
        {(offer?.quantity || 0).toString()}
      </Badge>
      <p>
        {t('offerList.label')} <span className="sm-bold">{formatUserCurrency(offer?.convertedPrice || 0)}</span>
      </p>
      {user?.id === offer.offerId ? (
        <Button
          className="ml-auto"
          disabled={isPaymentBlocked}
          onClick={() => onEditOfferClick(offer)}
          variant={BUTTON_VARIANTS.TERTIARY}
        >
          {t('edit')}
        </Button>
      ) : (
        <Button
          className="ml-auto"
          disabled={isPaymentBlocked}
          onClick={() => onBuyOfferClick(offer)}
          variant={BUTTON_VARIANTS.PRIMARY}
        >
          {t('buy')}
        </Button>
      )}
    </div>
  );
};

const Wrapper = ({ children }: { children: React.ReactNode }) => (
  <div className={clx('w-full md:w-[424px] flex items-center justify-center')}>
    <div className="flex gap-2 flex-col overflow-auto pointer-events-auto bg-neutral-50 border-4 rounded-2xl p-2  w-full justify-center items-center">
      {children}
    </div>
  </div>
);

const ListOffer = ({ cardRef, withPagination, itemsPerPage }: Props) => {
  const { isMobileApp } = useConfig();

  const { queryResult, remainingItems, total, handleOpenManageOfferModal, handleOpenBuyOfferModal } = useListOffer({
    cardRef,
    itemsPerPage,
    withPagination,
    disabled: isMobileApp,
  });
  const { t } = useTranslation();
  const { user } = useUserApi();
  const { data: kycParams } = useApiParams();
  const isKYCEnabled = useFeatureFlag(PARAMS_KEYS.USER_KYC_ENABLED);
  const { formatUserCurrency } = useFormatPrice();

  if (isMobileApp) {
    return null;
  }

  if (queryResult.isLoading || queryResult.isFetching) {
    return (
      <Wrapper>
        <Spinner />
      </Wrapper>
    );
  }

  if (isKYCEnabled && user?.kycStatus === 'REQUIRED') {
    return (
      <Wrapper>
        <p className="title-4">{t('kyc.placeholder.title')}</p>
        <p>
          {t('kyc.placeholder.mkt.text', {
            amount: formatUserCurrency(Number(kycParams?.USER_KYC_AMOUNT_REQUIRED) / 100),
          })}
        </p>
        <Button href={CMS_ROUTING.verifyProfile} variant={BUTTON_VARIANTS.PRIMARY}>
          {t('kyc.cta.label')}
        </Button>
      </Wrapper>
    );
  }

  return (
    <Wrapper>
      {(!queryResult.data || queryResult.data?.data['hydra:totalItems'] === 0) && (
        <i className="fa-kit fa-altered-swirl text-4xl text-neutral-300 text-center" />
      )}
      {queryResult.data?.data['hydra:member'].map((offer) => (
        <OfferView
          key={offer['@id']}
          offer={offer}
          onBuyOfferClick={handleOpenBuyOfferModal}
          onEditOfferClick={handleOpenManageOfferModal}
        />
      ))}
      {withPagination ? (
        <Pagination itemsPerPage={itemsPerPage} totalItems={queryResult.data?.data['hydra:totalItems']} />
      ) : (
        <p className="text-neutral-600 text-center">
          {total === 0 ? (
            t('no-offers')
          ) : (
            <>
              <span className="md-bold">{remainingItems}</span> {t('offerList.remainingOffer')}
            </>
          )}
        </p>
      )}
    </Wrapper>
  );
};

export default ListOffer;
